import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Headphones, Compass, MessageCircle, Target, Sparkles, Clock, Zap, Play, Check, Coffee } from 'lucide-react';
import { Button } from '../ui/button';
import { programData, appScreenshots } from '../../data/mock';
import coffeeImage from '@/assets/images/coffee-contemplation.jpg';
import principlesImage from '@/assets/images/program-screen.png';
import audioImage from '@/assets/images/bedtime-screen.png';
import plannerImage from '@/assets/images/adioscope-screen.png';
const ProgramSection = () => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const iconMap = { Calendar, Headphones, Compass, MessageCircle, Target, Sparkles };

  return (
    <section 
      ref={sectionRef}
      className="py-32 bg-[#202020] relative"
    >
      <div className="max-w-5xl mx-auto px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <span className={`inline-block text-sm text-[#efede6]/40 uppercase tracking-widest mb-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            The Program
          </span>
          <h2 className={`text-3xl md:text-4xl lg:text-5xl font-normal text-[#efede6] mb-6 tracking-tight transition-all duration-1000 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {programData.title}
          </h2>
          <p className={`text-xl text-[#efede6]/50 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {programData.subtitle}
          </p>
        </div>

        {/* Components Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-20">
          {programData.components.map((component, index) => {
            const Icon = iconMap[component.icon];
            return (
              <div 
                key={index}
                className={`group bg-[#efede6]/5 rounded-2xl p-7 border border-[#efede6]/10 hover:bg-[#efede6]/10 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${(index + 2) * 80}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-[#efede6]/10 flex items-center justify-center mb-5 group-hover:bg-[#efede6]/20 transition-colors">
                  <Icon className="w-5 h-5 text-[#efede6]/60" />
                </div>
                <h3 className="text-lg font-medium text-[#efede6] mb-2">{component.title}</h3>
                <p className="text-[#efede6]/40 text-sm">{component.description}</p>
              </div>
            );
          })}
        </div>

        {/* App Screenshots */}
        <div className="mb-20">
          <h3 className={`text-2xl font-normal text-[#efede6] text-center mb-4 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {appScreenshots.title}
          </h3>
          <p className={`text-[#efede6]/40 text-center mb-12 transition-all duration-1000 delay-550 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {appScreenshots.subtitle}
          </p>

          {/* App Mockups */}
          <div className="grid md:grid-cols-3 gap-8">
            {appScreenshots.screens.map((screen, index) => (
              <div 
                key={index}
                className={`transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${(index + 8) * 80}ms` }}
              >
                {/* Phone Mockup */}
                <div className="bg-[#efede6]/5 rounded-3xl p-4 border border-[#efede6]/10 mb-5">
                  <div className="bg-[#efede6] rounded-2xl aspect-[9/16] flex items-center justify-center relative overflow-hidden">
                    <div className="absolute inset-0">
                      {screen.mockup === 'principles' && (
                        <div className="space-y-3">
                          <img src={principlesImage} alt="Principles" className="w-full h-full object-cover" />
                        </div>
                      )}
                      {screen.mockup === 'audio' && (
                        <div className="flex flex-col items-center">
                          <img src={audioImage} alt="Audio" className="w-full h-full object-cover" />
                        </div>
                      )}
                      {screen.mockup === 'planner' && (
                        <div className="space-y-3">
                          <img src={plannerImage} alt="Planner" className="w-full h-full object-cover" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <h4 className="text-[#efede6] font-medium text-center">{screen.title}</h4>
                <p className="text-[#efede6]/40 text-sm text-center">{screen.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Time & Access */}
        <div className={`flex flex-col md:flex-row items-center justify-center gap-6 mb-12 transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          
          <div className={`transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src={coffeeImage} 
                  alt="Stressed mother"
                  className="w-full h-[500px] object-cover grayscale brightness-90"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#202020]/40 to-transparent" />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 border border-[#efede6]/20 rounded-lg px-6 py-3">
            <Coffee className="w-6 h-6 text-[#efede6]/50" />
            <span className="text-[#efede6]/70 text-lg">{programData.timeCommitment1} <span className="font-bold">{programData.timeCommitment2}</span></span>
          </div>
          {/* <div className="flex items-center gap-3 border border-[#efede6]/20 rounded-lg px-6 py-3">
            <Zap className="w-4 h-4 text-[#efede6]/50" />
            <span className="text-[#efede6]/70 text-sm">{programData.access}</span>
          </div> */}
        </div>

        {/* Highlight */}
        <div className={`bg-[#efede6]/5 rounded-2xl p-10 text-center max-w-2xl mx-auto mb-12 border border-[#efede6]/10 transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <Check className="w-8 h-8 text-[#efede6]/50 mx-auto mb-4" />
          <p className="text-[#efede6] text-xl">
            {programData.highlight}
          </p>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button 
            onClick={() => navigate('/checkout')}
            className={`bg-[#efede6] hover:bg-white text-[#202020] font-medium text-base px-10 py-6 rounded-lg transition-all duration-300 hover:scale-105 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '800ms' }}
          >
            Become a Peaceful Parent
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ProgramSection;
